import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import unitStatusApi from '../services/unitStatusApi';
import { formatDate } from '../utils/dateFormatter';
import { toRiyadhInputValue, riyadhInputValueToIso } from '../utils/dateValueHelper';
import { STOP_REASON_LABELS, UNIT_STOP_UI as UI } from '../config/unitStatusLabels';

/**
 * «إيقاف الوحدة» and «تعديل الإيقاف» — tasks/unit-operational-status.md D13–D16, §9 items 7–8.
 *
 * ── Two steps when the calendar closes ────────────────────────────────────────────────────────
 * Stop sale is what ops rely on for every unit problem (founder), and closing a unit on the booking
 * platforms loses sales. So the first press states exactly which nights will stop selling; only the
 * second press does it. A stop that does not touch the calendar saves in one step.
 *
 * ── Defaults come from the server ─────────────────────────────────────────────────────────────
 * Review tomorrow 10:00 Riyadh and «إغلاق حتى تاريخ» = the review day. A browser's "tomorrow" is
 * wrong between 00:00 and 03:00 Riyadh for anyone outside it.
 *
 * Edit mode (`stop` given): the reason and the calendar switch are fixed; the closure can only be
 * pushed later — reopening is «استئناف»'s job alone.
 */
/** Browser validation text follows the browser's language; ours is always Arabic (walkthrough F2). */
const arabicValidity = (message) => ({
  onInvalid: (e) => e.target.setCustomValidity(e.target.validity.rangeUnderflow ? UI.closedThroughTooEarly : message),
  onInput: (e) => e.target.setCustomValidity(''),
});

const StopUnitModal = ({ show, onHide, propertyId, stop = null, businessDate, onSaved }) => {
  const editing = Boolean(stop);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [reasons, setReasons] = useState(Object.keys(STOP_REASON_LABELS));
  const [reasonCode, setReasonCode] = useState('maintenance');
  const [note, setNote] = useState('');
  const [reviewAt, setReviewAt] = useState('');
  const [closeOnChannels, setCloseOnChannels] = useState(true);
  const [closedThrough, setClosedThrough] = useState('');

  // Every open starts clean (the relocation modal's rule — a carried-over reason files the next stop
  // under a fault that never happened).
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setError(null);
    setConfirming(false);
    setSaving(false);

    if (editing) {
      setReasonCode(stop.reasonCode);
      setNote(stop.note || '');
      setReviewAt(toRiyadhInputValue(stop.reviewAt));
      setCloseOnChannels(Boolean(stop.closedThrough));
      setClosedThrough(stop.closedThrough || '');
      return undefined;
    }

    setReasonCode('maintenance');
    setNote('');
    setCloseOnChannels(true);
    setLoading(true);
    unitStatusApi.getStopDefaults()
      .then(({ defaults }) => {
        if (cancelled) return;
        setReviewAt(toRiyadhInputValue(defaults.reviewAt));
        setClosedThrough(defaults.closedThrough || '');
        if (Array.isArray(defaults.reasons) && defaults.reasons.length) setReasons(defaults.reasons);
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || UI.saveFailed); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, editing, stop]);

  const noteMissing = reasonCode === 'other' && !note.trim();
  const closingMore = editing && stop.closedThrough && closedThrough > stop.closedThrough;
  // The confirmation is for the calendar: a new closure, or extra nights on an existing one.
  const needsConfirm = editing ? closingMore : closeOnChannels;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = editing
        ? {
          reviewAt: riyadhInputValueToIso(reviewAt),
          note,
          ...(stop.closedThrough ? { closedThrough } : {}),
        }
        : {
          reasonCode,
          note,
          reviewAt: riyadhInputValueToIso(reviewAt),
          closeOnChannels,
          ...(closeOnChannels ? { closedThrough } : {}),
        };
      const res = editing ? await unitStatusApi.update(stop.stopId, body) : await unitStatusApi.stop(propertyId, body);
      onSaved?.(res);
      onHide();
    } catch (err) {
      // Shown verbatim — every refusal names what to change (a past date, a missing note, …).
      setError(err.response?.data?.error || UI.saveFailed);
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (noteMissing) return;
    if (needsConfirm && !confirming) { setConfirming(true); return; }
    save();
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered dir="rtl" className="stop-unit-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editing ? UI.editModalTitle : UI.stopModalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : confirming ? (
            <div className="stop-unit-confirm" role="alert">
              <i className="bi bi-exclamation-octagon" aria-hidden="true"></i>
              <div>
                <p className="stop-unit-confirm-lead">{editing ? UI.confirmExtendLead : UI.confirmStopLead}</p>
                <p className="stop-unit-confirm-range">
                  {editing
                    ? UI.confirmExtendTo(formatDate(closedThrough))
                    : UI.confirmStopRange(formatDate(businessDate), formatDate(closedThrough))}
                </p>
                <p className="stop-unit-confirm-keep">{UI.confirmStopKeep}</p>
              </div>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3" controlId="stopReason">
                <Form.Label>{UI.reason}</Form.Label>
                <Form.Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} disabled={editing}>
                  {reasons.map((code) => (
                    <option key={code} value={code}>{STOP_REASON_LABELS[code] || code}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="stopNote">
                <Form.Label>{UI.note}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={1000}
                  value={note}
                  placeholder={UI.notePlaceholder}
                  onChange={(e) => setNote(e.target.value)}
                  isInvalid={noteMissing}
                />
                <Form.Control.Feedback type="invalid">{UI.noteRequired}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="stopReviewAt">
                <Form.Label>{UI.reviewAt}</Form.Label>
                <Form.Control type="datetime-local" value={reviewAt} onChange={(e) => setReviewAt(e.target.value)} required dir="ltr" {...arabicValidity(UI.fieldRequired)} />
                <Form.Text muted>{UI.reviewHelp}</Form.Text>
              </Form.Group>

              {!editing && (
                <Form.Check
                  className="mb-2"
                  type="switch"
                  id="stopCloseOnChannels"
                  label={UI.closeOnChannels}
                  checked={closeOnChannels}
                  onChange={(e) => setCloseOnChannels(e.target.checked)}
                />
              )}

              {closeOnChannels && (!editing || stop.closedThrough) && (
                <Form.Group controlId="stopClosedThrough">
                  <Form.Label>{UI.closedThrough}</Form.Label>
                  <Form.Control
                    type="date"
                    value={closedThrough}
                    // Never a past night: an overdue stop's stored date may already be behind today.
                    min={editing && stop.closedThrough > businessDate ? stop.closedThrough : businessDate}
                    onChange={(e) => setClosedThrough(e.target.value)}
                    required
                    dir="ltr"
                    {...arabicValidity(UI.fieldRequired)}
                  />
                  <Form.Text muted>{UI.closedThroughHelp}</Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {confirming ? (
            <>
              <Button variant="outline-secondary" onClick={() => setConfirming(false)} disabled={saving}>{UI.back}</Button>
              <Button type="submit" variant="danger" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : UI.confirmStop}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={onHide} disabled={saving}>{UI.cancel}</Button>
              <Button type="submit" variant={editing ? 'primary' : 'danger'} disabled={saving || loading || noteMissing}>
                {saving ? <Spinner animation="border" size="sm" /> : (editing ? UI.saveEdit : UI.stop)}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StopUnitModal;
